import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffortsCorrelation } from '../hooks/useEffortsDashboard';
import { formatNumber } from '../../../utils/dashboardFormatters';
import {
  FIRST_COL_SX,
  HEADER_CELL_SX,
  NUM_CELL_SX,
  TABLE_CARD_SX,
  TABLE_SX,
  TABLE_WRAP_SX,
  TableCardTitle,
  TableFooter,
} from './EffortsTableParts.jsx';
import {
  achScore,
  coverageScore,
  complianceScore,
  achTextColor,
  covTextColor,
  compTextColor,
  CF_TEXT,
  TENURE_BANDS,
  tenureInBand,
} from './effortsTableStyles.js';

const ROWS_PER_PAGE = 50;
const DASH = '—';

/**
 * The 2×2 quadrants (reference `corr-box`): Sales High/Low × Effort High/Low.
 * `code` is the local HH/HL/LH/LL (Effort then Sales); `backendKey` maps to the
 * API `quadrants` object and `rows[].quadrant`. Layout order follows the
 * reference grid (Effort High row first).
 */
const QUADRANTS = [
  { code: 'HH', backendKey: 'highEffortHighSales', label: '★ Star', bg: '#e9f7ef', color: '#1f8f4e' },
  { code: 'HL', backendKey: 'highEffortLowSales', label: 'Effort↑ Sales↓', bg: '#eef3fe', color: '#3567d6' },
  { code: 'LH', backendKey: 'lowEffortHighSales', label: 'Effort↓ Sales↑', bg: '#fdf4e3', color: '#b7791f' },
  { code: 'LL', backendKey: 'lowEffortLowSales', label: 'Watch', bg: '#fdECEC', color: '#d64545' },
];

/** First defined value among the candidates. */
const pick = (...vals) => {
  for (const v of vals) if (v != null && v !== '') return v;
  return null;
};

const num = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));
const money = (v) => (num(v) == null ? DASH : formatNumber(num(v)));
const pct1 = (v) => (num(v) == null ? DASH : `${num(v).toFixed(1)}%`);
const score1 = (v) => (num(v) == null ? DASH : num(v).toFixed(1));

/** Coloured-text span; plain when no colour. */
function CT({ color, children }) {
  return (
    <Box component="span" sx={color ? { color, fontWeight: 800 } : undefined}>
      {children}
    </Box>
  );
}

/**
 * Derive the full per-FLS correlation row the table needs. Reads whatever the
 * backend sends (many field-name fallbacks) and computes the HTML's scores:
 *   Sales Score  = 0.7·TotalAchScore + 0.3·PowerBrandAchScore
 *   Effort Score = 0.5·CoverageScore + 0.5·ComplianceScore
 *   High when score ≥ 4.
 * Power-brand target/CY/ach are NOT backend-sourced yet → kept null (greyed
 * em dash); their Ach Score falls back to the Total Ach Score (reference `pas`).
 */
function deriveRow(r) {
  const totalTarget = num(pick(r.totalTarget, r.total_target, r.tgt, r.target));
  const cySales = num(pick(r.cySales, r.cy_sales, r.cy, r.sales));
  const totalAch = num(
    pick(r.totalAchPct, r.total_ach_pct, r.totalAch, r.salesAchievementPct)
  );
  // Power-brand trio: not sourced from the efforts backend yet.
  const pbTarget = num(pick(r.powerBrandTarget, r.power_brand_target, r.pbTgt));
  const pbCy = num(pick(r.powerBrandCySales, r.power_brand_cy_sales, r.pbCy));
  const pbAch = num(pick(r.powerBrandAchPct, r.power_brand_ach_pct, r.pbAch));

  const coverage = num(
    pick(r.coveragePercent, r.coveragePercentRaw, r.coverage, r.covAvg)
  );
  const compliance = num(
    pick(r.compliancePercent, r.compliancePercentRaw, r.compliance, r.compAvg)
  );

  const tas = achScore(totalAch);
  const pas = pbAch == null ? tas : achScore(pbAch);
  const salesScore = 0.7 * tas + 0.3 * pas;
  const cs = coverageScore(coverage);
  const fs = complianceScore(compliance);
  const effortScore = 0.5 * cs + 0.5 * fs;

  return {
    ...r,
    division: pick(r.division, r.div),
    totalTarget,
    cySales,
    totalAch,
    pbTarget,
    pbCy,
    pbAch,
    coverage,
    compliance,
    tas,
    pas,
    salesScore,
    cs,
    fs,
    effortScore,
    salesHL: salesScore >= 4 ? 'High' : 'Low',
    effortHL: effortScore >= 4 ? 'High' : 'Low',
    quadCode: (effortScore >= 4 ? 'H' : 'L') + (salesScore >= 4 ? 'H' : 'L'),
  };
}

export default function EffortVsPerformanceTab() {
  const [activeQuad, setActiveQuad] = useState(null); // HH|HL|LH|LL|null
  const [tenureBand, setTenureBand] = useState('all');
  const [page, setPage] = useState(0);

  // The effort axis is fixed to both coverage + compliance now (the table and
  // scores use both), so no axis toggle. Correlation still reads the applied
  // filters for refetch.
  const { data, isLoading, isError } = useEffortsCorrelation({
    effortAxis: 'coverage_percent',
  });

  const derived = useMemo(() => (data?.rows ?? []).map(deriveRow), [data]);

  // Apply the tenure band first, then the quadrant filter. Quadrant counts are
  // computed from the tenure-filtered set (so the matrix matches the table).
  const tenureFiltered = useMemo(
    () => derived.filter((r) => tenureInBand(r.tenureYears, tenureBand)),
    [derived, tenureBand]
  );

  const quadCounts = useMemo(() => {
    const q = { HH: 0, HL: 0, LH: 0, LL: 0 };
    tenureFiltered.forEach((r) => {
      q[r.quadCode] = (q[r.quadCode] ?? 0) + 1;
    });
    return q;
  }, [tenureFiltered]);

  const visibleRows = useMemo(() => {
    const rows = activeQuad
      ? tenureFiltered.filter((r) => r.quadCode === activeQuad)
      : tenureFiltered;
    // Tenure oldest → newest (reference sort).
    return [...rows].sort((a, b) => (b.tenureYears ?? -Infinity) - (a.tenureYears ?? -Infinity));
  }, [tenureFiltered, activeQuad]);

  // Reset to page 1 when the dataset, tenure band or quadrant changes.
  const lastKeyRef = useRef(null);
  const key = `${derived.length}|${tenureBand}|${activeQuad}`;
  if (lastKeyRef.current !== key) {
    lastKeyRef.current = key;
    if (page !== 0) setPage(0);
  }

  const pageCount = Math.max(1, Math.ceil(visibleRows.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pagedRows = visibleRows.slice(
    safePage * ROWS_PER_PAGE,
    safePage * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  const periodLabel = data?.periodLabel ?? data?.month ?? '';
  const subtitle =
    `${visibleRows.length} FLS${activeQuad ? ` · quadrant ${activeQuad}` : ''}` +
    ` · Sales by MIS HQ${periodLabel ? ` · ${periodLabel}` : ''}` +
    ' · Sales Score = 70% Total Ach + 30% Power Brand' +
    ' · Effort Score = 50% Coverage + 50% Compliance · High ≥ 4';

  const hl = (v) => (
    <Box component="span" sx={{ color: v === 'High' ? CF_TEXT[2] : CF_TEXT[0], fontWeight: 800 }}>
      {v}
    </Box>
  );

  return (
    <Box>
      {/* Heading + subheading */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 800, color: '#0F1E3D' }}>
          Effort ↔ Performance Correlation — FLS level
        </Typography>
        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: 0.25 }}>
          {subtitle}
        </Typography>
      </Box>

      {/* Tenure Band dropdown */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6B7280' }}>
          Tenure Band
        </Typography>
        <FormControl size="small">
          <Select
            value={tenureBand}
            onChange={(e) => setTenureBand(e.target.value)}
            sx={{ fontSize: '12px', minWidth: 150 }}
          >
            {TENURE_BANDS.map((t) => (
              <MenuItem key={t.value} value={t.value} sx={{ fontSize: '12px' }}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading ? <Alert severity="info">Loading effort vs performance…</Alert> : null}
      {isError ? <Alert severity="error">Failed to load effort vs performance.</Alert> : null}

      {!isLoading && !isError ? (
        <>
          {/* 2×2 matrix: Sales High/Low (cols) × Effort High/Low (rows). */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '110px 1fr 1fr',
              gap: 1,
              maxWidth: 560,
              mb: 1,
            }}
          >
            <Box />
            <Box sx={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#5b6472' }}>
              Sales High
            </Box>
            <Box sx={{ textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#5b6472' }}>
              Sales Low
            </Box>

            {/* Effort High row: HH, HL */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#5b6472' }}>
              Effort High
            </Box>
            {['HH', 'HL'].map((code) => {
              const q = QUADRANTS.find((x) => x.code === code);
              const active = activeQuad === code;
              return (
                <ButtonBase
                  key={code}
                  onClick={() => setActiveQuad(active ? null : code)}
                  aria-label={`${q.label}: ${quadCounts[code]} FLS`}
                  sx={{
                    display: 'block',
                    borderRadius: '12px',
                    p: '14px 10px',
                    textAlign: 'center',
                    bgcolor: q.bg,
                    color: q.color,
                    border: '2px solid',
                    borderColor: active ? q.color : 'transparent',
                  }}
                >
                  <Typography sx={{ fontSize: '24px', fontWeight: 800, lineHeight: 1 }}>
                    {quadCounts[code]}
                  </Typography>
                  <Typography sx={{ fontSize: '10.5px', fontWeight: 700, mt: '4px', opacity: 0.85 }}>
                    {q.label}
                  </Typography>
                </ButtonBase>
              );
            })}

            {/* Effort Low row: LH, LL */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#5b6472' }}>
              Effort Low
            </Box>
            {['LH', 'LL'].map((code) => {
              const q = QUADRANTS.find((x) => x.code === code);
              const active = activeQuad === code;
              return (
                <ButtonBase
                  key={code}
                  onClick={() => setActiveQuad(active ? null : code)}
                  aria-label={`${q.label}: ${quadCounts[code]} FLS`}
                  sx={{
                    display: 'block',
                    borderRadius: '12px',
                    p: '14px 10px',
                    textAlign: 'center',
                    bgcolor: q.bg,
                    color: q.color,
                    border: '2px solid',
                    borderColor: active ? q.color : 'transparent',
                  }}
                >
                  <Typography sx={{ fontSize: '24px', fontWeight: 800, lineHeight: 1 }}>
                    {quadCounts[code]}
                  </Typography>
                  <Typography sx={{ fontSize: '10.5px', fontWeight: 700, mt: '4px', opacity: 0.85 }}>
                    {q.label}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#8a93a3', mb: 2 }}>
            Click a quadrant to filter · click again to clear
          </Typography>

          {visibleRows.length === 0 ? (
            <Alert severity="info">No FLS to show.</Alert>
          ) : (
            <Box sx={TABLE_CARD_SX}>
              <TableCardTitle title="FLS correlation" count={visibleRows.length} countLabel="FLS" />
              <TableContainer sx={{ ...TABLE_WRAP_SX, maxHeight: 520 }}>
                <Table size="small" stickyHeader sx={TABLE_SX}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={HEADER_CELL_SX}>Division</TableCell>
                      <TableCell sx={HEADER_CELL_SX}>FLS Emp. Code</TableCell>
                      <TableCell sx={HEADER_CELL_SX}>FLS Name</TableCell>
                      <TableCell sx={HEADER_CELL_SX}>Date of Joining</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Tenure in Years</TableCell>
                      <TableCell sx={HEADER_CELL_SX}>MIS HQ</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Total Target</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>CY Sales</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Total Ach%</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Power Brand Target</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Power Brand CY Sales</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Power Brand Ach %</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Total Ach Score</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Power Brand Ach Score</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Sales Score</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Doctor Coverage</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Freq. Compliance</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Doctor Coverage Score</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Freq. Compliance Score</TableCell>
                      <TableCell align="right" sx={HEADER_CELL_SX}>Effort Score</TableCell>
                      <TableCell sx={HEADER_CELL_SX}>Sales (High/Low)</TableCell>
                      <TableCell sx={HEADER_CELL_SX}>Effort (High/Low)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRows.map((r, i) => (
                      <TableRow key={r.employeeCode ?? `${r.name}-${i}`} hover>
                        <TableCell>{r.division ?? DASH}</TableCell>
                        <TableCell sx={FIRST_COL_SX}>{r.employeeCode ?? DASH}</TableCell>
                        <TableCell sx={{ ...FIRST_COL_SX, whiteSpace: 'nowrap' }}>{r.name ?? DASH}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.joiningDate ?? DASH}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>
                          {r.tenureYears == null ? DASH : Number(r.tenureYears).toFixed(1)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.misHq ?? DASH}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>{money(r.totalTarget)}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>{money(r.cySales)}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>
                          <CT color={achTextColor(r.totalAch)}>{pct1(r.totalAch)}</CT>
                        </TableCell>
                        {/* Power-brand trio — not backend-sourced → greyed em dash */}
                        <TableCell align="right" sx={{ ...NUM_CELL_SX, color: '#b4bcc8' }}>{DASH}</TableCell>
                        <TableCell align="right" sx={{ ...NUM_CELL_SX, color: '#b4bcc8' }}>{DASH}</TableCell>
                        <TableCell align="right" sx={{ ...NUM_CELL_SX, color: '#b4bcc8' }}>{DASH}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>{r.tas}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>{r.pas}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>{score1(r.salesScore)}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>
                          <CT color={covTextColor(r.coverage)}>{pct1(r.coverage)}</CT>
                        </TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>
                          <CT color={compTextColor(r.compliance)}>{pct1(r.compliance)}</CT>
                        </TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>{r.cs}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>{r.fs}</TableCell>
                        <TableCell align="right" sx={NUM_CELL_SX}>{score1(r.effortScore)}</TableCell>
                        <TableCell>{hl(r.salesHL)}</TableCell>
                        <TableCell>{hl(r.effortHL)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TableFooter
                page={safePage}
                pageSize={ROWS_PER_PAGE}
                total={visibleRows.length}
                onPageChange={setPage}
              />
            </Box>
          )}
        </>
      ) : null}
    </Box>
  );
}
